'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Crown,
  Lightbulb,
  TrendingUp,
  Rocket,
  Check,
  ArrowRight,
  Quote,
  Briefcase,
  Target,
  Shield,
  Zap,
  DollarSign,
  LineChart,
  Users,
  Star,
  FileText,
  Calendar,
  CheckCircle2,
  Clock,
  BarChart3,
} from 'lucide-react';

interface StakeholderProfile {
  id: string;
  role: string;
  tagline: string;
  icon: React.ReactNode;
  gradient: string;
  accentColor: string;
  persona: {
    name: string;
    background: string;
    avatar: string; // Will be stylized gradient initials
  };
  transformation: {
    before: string;
    after: string;
  };
  pullQuote: string;
  keyBenefits: { icon: React.ReactNode; text: string }[];
  stats: { value: string; label: string }[];
  cta: string;
}

// Role-specific dashboard previews
function DashboardPreview({
  profileId,
  gradient,
}: {
  profileId: string;
  gradient: string;
}) {
  const dashboards: Record<string, React.ReactNode> = {
    // Founder Dashboard - Shows funding progress, active engagements, and milestones
    builders: (
      <div className="bg-gray-900 rounded-2xl p-5 overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-xs text-gray-500 ml-2">Founder Dashboard</span>
        </div>
        <div className="space-y-3">
          {/* Funding progress */}
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400">Funding Progress</span>
              <span className="text-xs text-purple-400 font-medium">72%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full w-[72%] bg-gradient-to-r from-purple-500 to-violet-500 rounded-full" />
            </div>
          </div>
          {/* Active engagements */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-3 h-3 text-amber-400" />
                <span className="text-[10px] text-gray-400">Investor</span>
              </div>
              <p className="text-xs text-white font-medium truncate">
                Series A Target
              </p>
              <div className="flex items-center gap-1 mt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-[9px] text-green-400">Active</span>
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-3 h-3 text-purple-400" />
                <span className="text-[10px] text-gray-400">Milestone</span>
              </div>
              <p className="text-xs text-white font-medium truncate">
                Product Launch
              </p>
              <div className="flex items-center gap-1 mt-1">
                <Clock className="w-2.5 h-2.5 text-amber-400" />
                <span className="text-[9px] text-amber-400">2 days left</span>
              </div>
            </div>
          </div>
          {/* Milestones */}
          <div className="bg-gray-800 rounded-lg p-3">
            <span className="text-[10px] text-gray-400">
              Investor Readiness
            </span>
            <div className="flex items-center gap-2 mt-2">
              {['Pitch', 'Model', 'Deck', 'Intro'].map((m, i) => (
                <div key={m} className="flex-1 text-center">
                  <div
                    className={cn(
                      'w-6 h-6 rounded-full mx-auto mb-1 flex items-center justify-center',
                      i < 2 ? 'bg-purple-500' : 'bg-gray-700'
                    )}
                  >
                    {i < 2 ? (
                      <Check className="w-3 h-3 text-white" />
                    ) : (
                      <span className="text-[9px] text-gray-500">{i + 1}</span>
                    )}
                  </div>
                  <span className="text-[8px] text-gray-500">{m}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),

    // Investor Dashboard - Shows deal pipeline, portfolio, and diligence requests
    investors: (
      <div className="bg-gray-900 rounded-2xl p-5 overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-xs text-gray-500 ml-2">Investor Dashboard</span>
        </div>
        <div className="space-y-3">
          {/* Deal pipeline */}
          <div className="bg-gray-800 rounded-lg p-3">
            <span className="text-[10px] text-gray-400">Deal Pipeline</span>
            <div className="flex items-center justify-between mt-2">
              {[
                { label: 'New', count: 12, color: 'bg-amber-500' },
                { label: 'Review', count: 5, color: 'bg-purple-500' },
                { label: 'Diligence', count: 3, color: 'bg-violet-500' },
                { label: 'Closed', count: 8, color: 'bg-green-500' },
              ].map((stage) => (
                <div key={stage.label} className="text-center">
                  <div
                    className={cn(
                      'w-8 h-8 rounded-lg mx-auto mb-1 flex items-center justify-center',
                      stage.color
                    )}
                  >
                    <span className="text-xs font-bold text-white">
                      {stage.count}
                    </span>
                  </div>
                  <span className="text-[8px] text-gray-500">
                    {stage.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* Featured deals */}
          <div className="space-y-2">
            {[
              { name: 'TechVenture AI', sector: 'Fintech', match: '94%' },
              { name: 'GreenLogix', sector: 'CleanTech', match: '87%' },
            ].map((deal) => (
              <div
                key={deal.name}
                className="bg-gray-800 rounded-lg p-2 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Rocket className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white font-medium">
                      {deal.name}
                    </p>
                    <p className="text-[9px] text-gray-500">{deal.sector}</p>
                  </div>
                </div>
                <span className="text-xs text-amber-400 font-medium">
                  {deal.match}
                </span>
              </div>
            ))}
          </div>
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-800 rounded-lg p-2 text-center">
              <p className="text-sm font-bold text-white">$2.4M</p>
              <p className="text-[8px] text-gray-500">Deployed</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-2 text-center">
              <p className="text-sm font-bold text-amber-400">3.2x</p>
              <p className="text-[8px] text-gray-500">Avg Return</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-2 text-center">
              <p className="text-sm font-bold text-white">12</p>
              <p className="text-[8px] text-gray-500">Portfolio</p>
            </div>
          </div>
        </div>
      </div>
    ),
  };

  return dashboards[profileId] || null;
}

const stakeholders: StakeholderProfile[] = [
  {
    id: 'builders',
    role: 'For Founders',
    tagline: 'Buy clarity, execution support, and fundability',
    icon: <Rocket className="w-7 h-7" />,
    gradient: 'from-purple-600 to-violet-600',
    accentColor: 'purple',
    persona: {
      name: 'Alex Chen',
      background: 'First-time founder, ex-Product Manager',
      avatar: 'AC',
    },
    transformation: {
      before:
        'Struggling to articulate value proposition, unclear path to funding',
      after:
        'Raised $2.4M seed with confident pitch and validated business model',
    },
    pullQuote:
      "Purple didn't just help me find investors—they helped me become investable.",
    keyBenefits: [
      {
        icon: <Users className="w-4 h-4" />,
        text: 'Expert guidance and mentorship',
      },
      {
        icon: <Target className="w-4 h-4" />,
        text: 'Investor-ready positioning',
      },
      {
        icon: <LineChart className="w-4 h-4" />,
        text: 'Market research and validation',
      },
      {
        icon: <Shield className="w-4 h-4" />,
        text: 'Escrow-protected engagements',
      },
    ],
    stats: [
      { value: '85%', label: 'Funding success' },
      { value: '3x', label: 'Avg. valuation lift' },
    ],
    cta: 'Apply as Builders',
  },
  {
    id: 'investors',
    role: 'For Investors',
    tagline: 'Buy decision confidence and leverage',
    icon: <TrendingUp className="w-7 h-7" />,
    gradient: 'from-amber-500 to-orange-500',
    accentColor: 'amber',
    persona: {
      name: 'Sarah Kim',
      background: 'Angel investor, 15+ portfolio companies',
      avatar: 'SK',
    },
    transformation: {
      before: 'Overwhelmed by deal flow, missed quality opportunities',
      after: 'Focused portfolio with pre-vetted deals and on-demand diligence',
    },
    pullQuote: 'The diligence support alone has saved me from three bad deals.',
    keyBenefits: [
      {
        icon: <Star className="w-4 h-4" />,
        text: 'Curated, pre-vetted deal flow',
      },
      {
        icon: <Briefcase className="w-4 h-4" />,
        text: 'On-demand diligence support',
      },
      {
        icon: <Lightbulb className="w-4 h-4" />,
        text: 'Research and analysis on request',
      },
      {
        icon: <Zap className="w-4 h-4" />,
        text: 'Pattern intelligence across deals',
      },
    ],
    stats: [
      { value: '3x', label: 'Deal quality' },
      { value: '60%', label: 'Time saved' },
    ],
    cta: 'Apply as Capital Investor',
  },
];

// Individual stakeholder showcase component
function StakeholderShowcase({
  profile,
  isActive,
}: {
  profile: StakeholderProfile;
  isActive: boolean;
}) {
  const accentColors = {
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-100',
      text: 'text-purple-600',
      badge: 'bg-purple-100 text-purple-700',
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-600',
      badge: 'bg-amber-100 text-amber-700',
    },
    violet: {
      bg: 'bg-violet-50',
      border: 'border-violet-100',
      text: 'text-violet-600',
      badge: 'bg-violet-100 text-violet-700',
    },
    fuchsia: {
      bg: 'bg-fuchsia-50',
      border: 'border-fuchsia-100',
      text: 'text-fuchsia-600',
      badge: 'bg-fuchsia-100 text-fuchsia-700',
    },
  };

  const colors = accentColors[profile.accentColor as keyof typeof accentColors];

  return (
    <div
      className={cn(
        'transition-all duration-500',
        isActive
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 translate-x-8 absolute pointer-events-none'
      )}
    >
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Left: Persona & Story */}
        <div>
          {/* Role badge */}
          <div
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6',
              colors.badge
            )}
          >
            {profile.icon}
            <span className="font-semibold text-sm">{profile.role}</span>
          </div>

          {/* Tagline */}
          <h3
            className="font-display font-semibold text-gray-900 mb-6"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}
          >
            {profile.tagline}
          </h3>

          {/* Persona card */}
          <div
            className={cn(
              'rounded-2xl p-6 mb-6',
              colors.bg,
              colors.border,
              'border'
            )}
          >
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div
                className={cn(
                  'w-16 h-16 rounded-2xl flex items-center justify-center text-white font-display font-bold text-xl bg-gradient-to-br',
                  profile.gradient
                )}
              >
                {profile.persona.avatar}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">
                  {profile.persona.name}
                </p>
                <p className="text-sm text-gray-500">
                  {profile.persona.background}
                </p>
              </div>
            </div>

            {/* Transformation */}
            <div className="mt-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-gray-500">B</span>
                </div>
                <p className="text-sm text-gray-600">
                  {profile.transformation.before}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-gradient-to-br',
                    profile.gradient
                  )}
                >
                  <span className="text-xs font-bold text-white">A</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {profile.transformation.after}
                </p>
              </div>
            </div>
          </div>

          {/* Pull quote */}
          <div className="relative pl-6 mb-8">
            <Quote
              className={cn('absolute left-0 top-0 w-4 h-4', colors.text)}
            />
            <p className="font-serif text-lg italic text-gray-700 leading-relaxed">
              "{profile.pullQuote}"
            </p>
          </div>

          {/* CTA */}
          <a
            href={
              profile.id === 'builders'
                ? '/apply/founder'
                : profile.id === 'investors'
                  ? '/apply/investor'
                  : profile.id === 'robinhoods'
                    ? '/apply/robin-hood'
                    : profile.id === 'insights'
                      ? '/apply/zero-associate'
                      : '#apply'
            }
          >
            <Button
              size="lg"
              className={cn(
                'bg-gradient-to-r text-white shadow-lg hover:shadow-xl transition-all group',
                profile.gradient
              )}
            >
              <span className="flex items-center gap-2">
                {profile.cta}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Button>
          </a>
        </div>

        {/* Right: Benefits & Stats */}
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            {profile.stats.map((stat, i) => (
              <div
                key={i}
                className={cn(
                  'rounded-xl p-5 text-center',
                  colors.bg,
                  colors.border,
                  'border'
                )}
              >
                <p
                  className={cn(
                    'text-3xl font-display font-bold bg-gradient-to-r bg-clip-text text-transparent',
                    profile.gradient
                  )}
                >
                  {stat.value}
                </p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-4">What You Get</h4>
            <div className="space-y-4">
              {profile.keyBenefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div
                    className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                      colors.bg,
                      colors.text
                    )}
                  >
                    {benefit.icon}
                  </div>
                  <span className="text-gray-700 pt-1">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Mini dashboard preview - Role specific */}
          <DashboardPreview
            profileId={profile.id}
            gradient={profile.gradient}
          />
        </div>
      </div>
    </div>
  );
}

// Navigation tabs
function NavigationTabs({
  stakeholders,
  activeId,
  onSelect,
}: {
  stakeholders: StakeholderProfile[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2 mb-12">
      {stakeholders.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          className={cn(
            'flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300',
            activeId === s.id
              ? `bg-gradient-to-r ${s.gradient} text-white shadow-lg`
              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:shadow'
          )}
        >
          {s.icon}
          <span className="hidden sm:inline">{s.role.replace('For ', '')}</span>
        </button>
      ))}
    </div>
  );
}

export default function StakeholdersSection() {
  const [activeId, setActiveId] = useState('builders');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeProfile = stakeholders.find((s) => s.id === activeId)!;

  return (
    <section
      ref={sectionRef}
      className="relative py-16 lg:py-20 overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, hsl(var(--warm-white)) 0%, hsl(270 50% 98%) 50%, hsl(var(--warm-white)) 100%)',
        }}
      />

      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-20 right-10 w-64 h-64 rounded-full opacity-30"
          style={{
            background:
              'radial-gradient(circle, hsl(271 81% 56% / 0.08) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute bottom-20 left-10 w-48 h-48 rounded-full opacity-20"
          style={{
            background:
              'radial-gradient(circle, hsl(38 92% 50% / 0.1) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div
            className={cn(
              'transition-all duration-700',
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8'
            )}
          >
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider mb-4">
              Your Role
            </p>
            <h2
              className="font-display font-semibold text-gray-900 mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Find Your Place in the{' '}
              <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                Ecosystem
              </span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Whether you're building, investing, advising, or
              analyzing—discover how Purple creates value for participants like
              you.
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div
          className={cn(
            'transition-all duration-700 delay-200',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <NavigationTabs
            stakeholders={stakeholders}
            activeId={activeId}
            onSelect={setActiveId}
          />
        </div>

        {/* Content area */}
        <div
          className={cn(
            'relative min-h-[700px] transition-all duration-700 delay-300',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          {stakeholders.map((profile) => (
            <StakeholderShowcase
              key={profile.id}
              profile={profile}
              isActive={activeId === profile.id}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
